/*
 * Scentt core \u2013 shared, dependency-free helpers for every Scentt component.
 * Exposes one frozen namespace: window.Scentt. Everything else stays private.
 */
(() => {
  // Already set up by an earlier copy of this file. A page element with id/name "Scentt" (staff HTML) also shows up
  // as window.Scentt, so only the real, frozen API counts; anything else is replaced below.
  const prev = window.Scentt;
  if (prev && Object.isFrozen(prev) && typeof prev.html === 'function' && typeof prev.list === 'function') return;

  /* ---------- small safe helpers ---------- */
  const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;', '`': '&#96;' };
  const esc = (value) => String(value == null ? '' : value).replace(/[&<>"'`]/g, (c) => ESC[c]);

  /** Tagged template: every ${value} is HTML-escaped. Arrays are joined (already-built html allowed via Scentt.raw). */
  const RAW = Symbol('scentt-raw');
  const raw = (htmlString) => ({ [RAW]: String(htmlString) });
  const piece = (v) => (v && v[RAW] !== undefined ? v[RAW] : Array.isArray(v) ? v.map(piece).join('') : esc(v));
  const html = (strings, ...values) => strings.reduce((out, s, i) => out + s + (i < values.length ? piece(values[i]) : ''), '');

  const int = (value, def = 0, min = -Infinity, max = Infinity) => {
    const n = Number.parseInt(value, 10);
    if (!Number.isFinite(n)) return def;
    return Math.min(max, Math.max(min, n));
  };
  const num = (value, def = 0) => {
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? n : def;
  };
  const oneOf = (value, list, def) => (list.includes(value) ? value : def);
  const isHandle = (value) => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/i.test(value);
  const isId = (value) => /^\d{1,20}$/.test(String(value));

  const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);
  const warn = (...args) => { if (designMode()) console.warn('[Scentt]', ...args); };

  const reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;

  /* ---------- config (read from data-* on #scentt-config, never from inline script) ---------- */
  const readConfig = () => {
    const el = document.getElementById('scentt-config');
    const d = el ? el.dataset : {};
    let root = d.root || '/';
    if (!root.startsWith('/') || root.startsWith('//')) root = '/';
    if (!root.endsWith('/')) root += '/';
    return Object.freeze({
      root,
      moneyFormat: d.moneyFormat || '{{amount}}',
      currency: /^[A-Z]{3}$/.test(d.currency || '') ? d.currency : '',
      cartUrl: d.cartUrl && d.cartUrl.startsWith('/') && !d.cartUrl.startsWith('//') ? d.cartUrl : '/cart',
      allUrl: d.allUrl && d.allUrl.startsWith('/') && !d.allUrl.startsWith('//') ? d.allUrl : '/collections/all',
      strings: Object.freeze({
        error: d.errorText || 'Something went wrong. Please try again.',
        offline: d.offlineText || 'You seem to be offline. Please check your connection.',
        added: d.addedText || 'Added to your bag',
        soldOut: d.soldOutText || 'Sorry, this item is sold out.',
      }),
    });
  };
  let config = null;
  const getConfig = () => (config = config || readConfig());

  /** Only same-origin, relative paths are ever fetched. */
  const url = (path) => {
    const p = String(path || '');
    /* Relative paths only: no protocol-relative (//), backslash tricks (/\\host) or control characters. */
    if (!p.startsWith('/') || p.startsWith('//') || /[\\\u0000-\u001f\u007f]/.test(p)) throw new Error('Blocked non-relative URL');
    const root = getConfig().root;
    const full = root !== '/' && !p.startsWith(root) ? root.replace(/\/$/, '') + p : p;
    if (new URL(full, window.location.origin).origin !== window.location.origin) throw new Error('Blocked cross-origin URL');
    return full;
  };

  /* ---------- storage (fails quietly in private mode / blocked storage) ---------- */
  const store = {
    get(key, validator, def) {
      try {
        const rawValue = window.localStorage.getItem(key);
        if (rawValue == null) return def;
        const parsed = JSON.parse(rawValue);
        return validator && !validator(parsed) ? def : parsed;
      } catch (e) {
        return def;
      }
    },
    set(key, value) {
      try { window.localStorage.setItem(key, JSON.stringify(value)); return true; } catch (e) { return false; }
    },
  };

  /** Validated list of product handles (wishlist, recently viewed, compare). */
  const lists = new Map();
  /* One-time import of lists saved by the previous theme (same store), so shoppers keep their wishlist. */
  const LEGACY = { 'scentt:wishlist': 'scented-wishlist', 'scentt:recent': 'scented-recent', 'scentt:compare': 'scented-compare' };
  const list = (key, max = 100) => {
    if (lists.has(key)) return lists.get(key);
    const listeners = new Set();
    const clean = (arr) => (Array.isArray(arr) ? arr.filter(isHandle) : []).filter((h, i, a) => a.indexOf(h) === i).slice(0, max);
    if (LEGACY[key] && !store.get(key, Array.isArray, null)) {
      const old = store.get(LEGACY[key], Array.isArray, []).map((x) => (x && typeof x === 'object' ? x.h : x));
      const moved = clean(old);
      if (moved.length) store.set(key, moved);
      try { window.localStorage.removeItem(LEGACY[key]); } catch (e) { /* storage blocked */ }
    }
    const read = () => clean(store.get(key, Array.isArray, []));
    const write = (arr) => { const v = clean(arr); store.set(key, v); listeners.forEach((fn) => fn(v)); emit('list', { key, items: v }); return v; };
    const api = Object.freeze({
      all: read,
      has: (h) => read().includes(h),
      add: (h, { front = true } = {}) => (isHandle(h) ? write(front ? [h, ...read().filter((x) => x !== h)] : [...read().filter((x) => x !== h), h]) : read()),
      remove: (h) => write(read().filter((x) => x !== h)),
      toggle: (h) => (read().includes(h) ? api.remove(h) : api.add(h, { front: false })),
      clear: () => write([]),
      onChange: (fn) => { listeners.add(fn); return () => listeners.delete(fn); },
      max,
    });
    window.addEventListener('storage', (e) => { if (e.key === key) { const v = read(); listeners.forEach((fn) => fn(v)); } });
    lists.set(key, api);
    return api;
  };

  /* ---------- events ---------- */
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(`scentt:${name}`, { detail }));
  const on = (name, fn) => { const h = (e) => fn(e.detail, e); document.addEventListener(`scentt:${name}`, h); return () => document.removeEventListener(`scentt:${name}`, h); };

  /* ---------- network (same origin, timeout, clear errors) ---------- */
  class ScenttError extends Error {
    constructor(message, { status = 0, userMessage = '' } = {}) { super(message); this.status = status; this.userMessage = userMessage; }
  }
  /* read(res) runs inside the timeout, so a stalled response body cannot hang a component. */
  const request = async (path, options = {}, read = (res) => res) => {
    if (!navigator.onLine) throw new ScenttError('offline', { userMessage: getConfig().strings.offline });
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), options.timeout || 10000);
    let res;
    try {
      res = await fetch(url(path), { credentials: 'same-origin', ...options, signal: controller.signal });
    } catch (e) {
      clearTimeout(timer);
      throw new ScenttError('network', { userMessage: getConfig().strings.error });
    }
    try {
      return await read(res);
    } catch (e) {
      if (e instanceof ScenttError) throw e;
      throw new ScenttError('network', { status: res.status, userMessage: getConfig().strings.error });
    } finally {
      clearTimeout(timer);
    }
  };
  const fetchSection = async (path, sectionId) => {
    if (!/^[a-z0-9_-]+$/i.test(sectionId)) throw new ScenttError('bad section id');
    const sep = path.includes('?') ? '&' : '?';
    const html = await request(`${path}${sep}section_id=${encodeURIComponent(sectionId)}`, { headers: { Accept: 'text/html' } }, async (res) => {
      if (!res.ok) throw new ScenttError(`HTTP ${res.status}`, { status: res.status, userMessage: getConfig().strings.error });
      return res.text();
    });
    return new DOMParser().parseFromString(html, 'text/html');
  };
  const fetchJSON = async (path, options = {}) => {
    const [res, data] = await request(path, { ...options, headers: { Accept: 'application/json', ...(options.headers || {}) } },
      async (r) => { let d = null; try { d = await r.json(); } catch (e) { if (e && e.name === 'AbortError') throw e; d = null; } return [r, d]; });
    if (!res.ok || !data || (data.status && data.status >= 400)) {
      const description = data && typeof data.description === 'string' ? data.description : '';
      throw new ScenttError(`HTTP ${res.status}`, { status: res.status, userMessage: description || getConfig().strings.error });
    }
    return data;
  };

  /* ---------- toasts (human-readable messages, aria-live) ---------- */
  class ScenttToast extends HTMLElement {
    connectedCallback() {
      this.setAttribute('role', 'status');
      this.setAttribute('aria-live', 'polite');
    }
    show(message, type = 'info') {
      const item = document.createElement('p');
      item.className = `scentt-toast__item scentt-toast__item--${oneOf(type, ['info', 'success', 'error'], 'info')}`;
      item.textContent = String(message).slice(0, 300);
      this.append(item);
      while (this.children.length > 3) this.firstElementChild.remove();
      setTimeout(() => item.classList.add('is-leaving'), 4200);
      setTimeout(() => item.remove(), 4600);
    }
  }
  if (!customElements.get('scentt-toast')) customElements.define('scentt-toast', ScenttToast);
  const toast = (message, type) => {
    let host = document.querySelector('scentt-toast');
    if (!host) { host = document.createElement('scentt-toast'); host.className = 'scentt-toast'; document.body.append(host); }
    host.show(message, type);
  };

  /* ---------- money ---------- */
  const money = (cents, format) => {
    const value = int(cents, 0);
    const fmt = String(format || getConfig().moneyFormat);
    const fixed = (n, d) => (n / 100).toFixed(d);
    const group = (s, t, dec) => { const [a, b] = s.split('.'); return a.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (b ? dec + b : ''); };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(fixed(value, 0), ',', '.');
        case 'amount_with_comma_separator': return group(fixed(value, 2), '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(fixed(value, 0), '.', ',');
        case 'amount_with_apostrophe_separator': return group(fixed(value, 2), "'", '.');
        default: return group(fixed(value, 2), ',', '.');
      }
    }).replace(/<[^>]*>/g, '');
  };

  /* ---------- cart (Shopify AJAX API, refreshes Trade's drawer / notification / bubble) ---------- */
  const cartUI = () => document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  const sectionsFor = () => {
    const ui = cartUI();
    const ids = ui && typeof ui.getSectionsToRender === 'function' ? ui.getSectionsToRender().map((s) => s.id) : [];
    if (!ids.includes('cart-icon-bubble')) ids.push('cart-icon-bubble');
    return ids.filter((id) => /^[a-z0-9_-]+$/i.test(id)).slice(0, 5);
  };
  const refreshBubble = (state) => {
    const fresh = state && state.sections && state.sections['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (!fresh || !bubble) return;
    const doc = new DOMParser().parseFromString(fresh, 'text/html');
    const inner = doc.querySelector('.shopify-section');
    if (inner) bubble.replaceChildren(...Array.from(inner.childNodes).map((n) => document.importNode(n, true)));
  };
  const onCartPage = () => document.body.classList.contains('template-cart') || /\/cart\/?$/.test(location.pathname);
  const afterCart = (state, { open = true, source = 'scentt' } = {}) => {
    emit('cart:change', { state, source });
    if (window.publish && window.PUB_SUB_EVENTS) { try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source, cartData: state }); } catch (e) { warn(e); } }
    if (onCartPage()) { window.location.reload(); return state; }
    const ui = cartUI();
    if (open && ui && typeof ui.renderContents === 'function' && state && state.sections) {
      /* Trade's cart notification expects the added line's key at the top level (missing for items[] adds). */
      const first = Array.isArray(state.items) && state.items[0];
      const view = !state.key && first && first.key ? { ...state, key: first.key } : state;
      ui.classList.remove('is-empty');
      try {
        ui.renderContents(view);
      } catch (e) {
        warn(e);
        refreshBubble(state);
        toast(getConfig().strings.added, 'success');
      }
    } else {
      refreshBubble(state);
      if (open) toast(getConfig().strings.added, 'success');
    }
    return state;
  };
  const post = async (path, body) => {
    const payload = { ...body, sections: sectionsFor(), sections_url: window.location.pathname };
    return fetchJSON(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  };
  const fail = (e) => { toast(e && e.userMessage ? e.userMessage : getConfig().strings.error, 'error'); throw e; };
  const cart = Object.freeze({
    /** items: [{ id: variantId, quantity, properties? }] */
    add(items, opts = {}) {
      const safe = (Array.isArray(items) ? items : []).filter((i) => i && isId(i.id)).slice(0, 10).map((i) => {
        const line = { id: Number(i.id), quantity: int(i.quantity, 1, 1, 99) };
        if (i.properties && typeof i.properties === 'object') {
          line.properties = {};
          Object.keys(i.properties).slice(0, 5).forEach((k) => { if (/^_?[\w -]{1,40}$/.test(k)) line.properties[k] = String(i.properties[k]).slice(0, 250); });
        }
        return line;
      });
      if (!safe.length) return Promise.reject(new ScenttError('no items'));
      return post('/cart/add.js', { items: safe }).then((s) => afterCart(s, opts)).catch(fail);
    },
    change(key, quantity, opts = {}) {
      if (typeof key !== 'string' || !/^[\w:]{1,120}$/.test(key)) return Promise.reject(new ScenttError('bad key'));
      return post('/cart/change.js', { id: key, quantity: int(quantity, 0, 0, 99) }).then((s) => afterCart(s, { open: false, ...opts })).catch(fail);
    },
    attributes(obj, opts = {}) {
      const attributes = {};
      Object.keys(obj || {}).slice(0, 5).forEach((k) => { if (/^[\w -]{1,40}$/.test(k)) attributes[k] = String(obj[k]).slice(0, 500); });
      return post('/cart/update.js', { attributes }).then((s) => afterCart(s, { open: false, ...opts })).catch(fail);
    },
    /** Set several lines' quantities in one request ({ lineKey: quantity }, 0 removes): one page refresh on the cart
     *  page instead of one per line. */
    set(lines, opts = {}) {
      const updates = Object.create(null); /* line keys come from the page: no prototype keys */
      const src = lines && typeof lines === 'object' && !Array.isArray(lines) ? lines : {};
      Object.keys(src).slice(0, 20).forEach((k) => { if (/^[\w:]{1,120}$/.test(k)) updates[k] = int(src[k], 0, 0, 99); });
      if (!Object.keys(updates).length) return Promise.reject(new ScenttError('bad key'));
      return post('/cart/update.js', { updates }).then((s) => afterCart(s, { open: false, ...opts })).catch(fail);
    },
    get: () => fetchJSON('/cart.js'),
  });

  /* ---------- scheduling ---------- */
  const onIdle = (fn) => ('requestIdleCallback' in window ? window.requestIdleCallback(fn, { timeout: 2000 }) : setTimeout(fn, 200));
  const inView = (el, fn, rootMargin = '200px') => {
    if (!('IntersectionObserver' in window)) { fn(); return () => {}; }
    const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) { io.disconnect(); fn(); } }, { rootMargin });
    io.observe(el);
    return () => io.disconnect();
  };
  const debounce = (fn, ms = 150) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };

  /* ---------- forms sharing one Shopify form type (newsletter, notify me, reviews):
     show the thank-you / error message only on the form that was actually submitted ---------- */
  const FORM_KEY = 'scentt:form';
  document.addEventListener('submit', (e) => {
    const form = e.target && e.target.closest && e.target.closest('[data-scentt-form]');
    if (!form) return;
    try { window.sessionStorage.setItem(FORM_KEY, form.getAttribute('data-scentt-form')); } catch (err) { /* storage blocked */ }
  }, true);
  const revealForms = () => {
    let id = null;
    let storageOk = true;
    try { id = window.sessionStorage.getItem(FORM_KEY); } catch (err) { storageOk = false; }
    let shown = null;
    document.querySelectorAll('[data-scentt-success], [data-scentt-error]').forEach((el) => {
      const key = el.getAttribute('data-scentt-success') || el.getAttribute('data-scentt-error');
      if (storageOk && id !== key) return;
      el.hidden = false;
      shown = el;
      const form = el.closest('form');
      if (form && el.hasAttribute('data-scentt-success')) form.classList.add('is-done');
    });
    if (shown && id) {
      try { window.sessionStorage.removeItem(FORM_KEY); } catch (err) { /* ignore */ }
      setTimeout(() => shown.scrollIntoView({ block: 'center' }), 50);
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', revealForms); else revealForms();

  const api = Object.freeze({
    esc, html, raw, int, num, oneOf, isHandle, isId, warn, reducedMotion,
    get config() { return getConfig(); },
    url, store, list, emit, on, fetchSection, fetchJSON, toast, money, cart, onIdle, inView, debounce, Error: ScenttError,
  });
  // own, read-only property: shadows any element named "Scentt" and can't be overwritten later
  try {
    Object.defineProperty(window, 'Scentt', { value: api, writable: false, enumerable: false, configurable: false });
  } catch (e) {
    window.Scentt = api;
  }
})();
